import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { ConsultationType } from '@/core/models/consultation.model'

import ConsultationsSearchForm, { ConsultationsSearchFormProps, ConsultationsSearchFormValues } from '../components/ConsultationsSearchForm'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getConsultations, selectConsultationSearchParams } from '@/redux/reducers/consultation.reducer'
import { getPatients, selectPatients } from '@/redux/reducers/patient.reducer'

import { limitOptions } from './ConsultationsTable'

export type ConsultationsSearchFormContainerProps = Pick<ConsultationsSearchFormProps, 'direction'>

export function ConsultationsSearchFormContainer(props: ConsultationsSearchFormContainerProps) {
  const dispatch = useAppDispatch()

  const consultationSearchParams = useAppSelector(selectConsultationSearchParams)
  const patients = useAppSelector(selectPatients)

  const patientOptions = useMemo(
    () =>
      _.map(patients, (patient) => ({
        label: `${patient.userInfo.name} ${patient.userInfo.surname}`,
        value: patient._id,
      })),
    [patients]
  )

  const initialValues = useMemo(
    () => ({
      patient: '',
      type: ConsultationType.Local,
      startDate: {
        $gte: dayjs().subtract(1, 'months').toDate(),
        $lte: dayjs().add(1, 'months').toDate(),
      },
    }),
    []
  )

  useEffect(() => {
    dispatch(getConsultations(transformData(initialValues), { limit: limitOptions[0], order: 'DESC', orderBy: 'startDate' }))
  }, [dispatch, initialValues])

  const transformData = (values: ConsultationsSearchFormValues) => {
    return {
      patient: values.patient || undefined,
      type: ConsultationType.Local,
      startDate: {
        $gte: values.startDate.$gte ? dayjs(values.startDate.$gte).startOf('day').toISOString() : undefined,
        $lte: values.startDate.$lte ? dayjs(values.startDate.$lte).endOf('day').toISOString() : undefined,
      },
    }
  }

  const handleSearchConsultations = (values: ConsultationsSearchFormValues) => {
    const data = transformData(values)
    dispatch(getConsultations(data, { limit: consultationSearchParams?.options?.limit }))
  }

  const handleSearchPatient = useDebouncedCallback((value: string) => {
    dispatch(getPatients({ smartSearch: value || undefined }))
  }, 300)

  return (
    <ConsultationsSearchForm
      {...props}
      initialValues={initialValues}
      onReset={handleSearchConsultations}
      onSearchPatient={handleSearchPatient}
      onSubmit={handleSearchConsultations}
      patientOptions={patientOptions}
    />
  )
}

export default React.memo(ConsultationsSearchFormContainer)
